<template>
  <v-dialog v-model="dialog" max-width="750" persistent scrollable>
    <v-card class="settings-card">
      <v-card-title class="settings-header">
        <v-icon dark class="mr-2">mdi-cog</v-icon>
        <span class="text-h6 font-weight-bold">Setări Decontare</span>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-tabs v-model="activeTab" background-color="white" color="#2d8659" grow>
        <v-tab>
          <v-icon small class="mr-1">mdi-shape-outline</v-icon>
          Categorii
        </v-tab>
        <v-tab>
          <v-icon small class="mr-1">mdi-file-document-edit-outline</v-icon>
          Șabloane defecte
        </v-tab>
        <v-tab>
          <v-icon small class="mr-1">mdi-account-group</v-icon>
          Experți
        </v-tab>
        <v-tab>
          <v-icon small class="mr-1">mdi-account-multiple-check</v-icon>
          Comisia PV
        </v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <v-card-text ref="body" class="settings-body">
        <v-tabs-items v-model="activeTab">
          <!-- ═══ TAB 1: Categories ═══ -->
          <v-tab-item>
            <div class="tab-content">
              <!-- Add new -->
              <v-card flat class="add-row mb-4">
                <v-row dense align="center">
                  <v-col cols="6">
                    <v-text-field
                      v-model="newCategory"
                      label="Denumire categorie nouă"
                      outlined
                      dense
                      hide-details
                      placeholder="ex: Mobilier, Echipament rețea..."
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-text-field
                      v-model="newCategoryCod"
                      label="Cod catalog"
                      outlined
                      dense
                      hide-details
                      placeholder="ex: 148471"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-btn
                      color="#2d8659"
                      dark
                      block
                      small
                      :disabled="!newCategory.trim()"
                      @click="addCategory"
                      :loading="savingCategory"
                    >
                      <v-icon left small>mdi-plus</v-icon>
                      Adaugă
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card>

              <!-- List -->
              <div v-if="categories.length === 0" class="empty-state">
                Nicio categorie. Adăugați prima categorie mai sus.
              </div>
              <div
                v-for="cat in categories"
                :key="cat.id"
                class="list-item"
              >
                <template v-if="editingCategoryId === cat.id">
                  <v-row dense align="center">
                    <v-col cols="5">
                      <v-text-field
                        v-model="editCategoryValue"
                        label="Denumire"
                        outlined
                        dense
                        hide-details
                        autofocus
                        @keyup.enter="saveCategory(cat)"
                        @keyup.esc="cancelEditCategory"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="4">
                      <v-text-field
                        v-model="editCategoryCod"
                        label="Cod catalog"
                        outlined
                        dense
                        hide-details
                        @keyup.enter="saveCategory(cat)"
                        @keyup.esc="cancelEditCategory"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="3" class="d-flex">
                      <v-btn icon small color="success" @click="saveCategory(cat)" class="mr-1">
                        <v-icon small>mdi-check</v-icon>
                      </v-btn>
                      <v-btn icon small @click="cancelEditCategory">
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </template>
                <template v-else>
                  <div class="list-item-content">
                    <span class="list-item-text">{{ cat.denumire }}<span v-if="cat.cod_catalog" class="grey--text text--darken-1"> — {{ cat.cod_catalog }}</span></span>
                    <div class="list-item-actions">
                      <v-btn icon x-small @click="startEditCategory(cat)">
                        <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon x-small @click="removeCategory(cat)">
                        <v-icon small color="red lighten-1">mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </v-tab-item>

          <!-- ═══ TAB 2: Templates ═══ -->
          <v-tab-item>
            <div class="tab-content">
              <p class="hint-text mb-3">
                Folosiți <code>{denumirea}</code>, <code>{categoria}</code>, <code>{departament}</code> ca variabile parametrice.
              </p>

              <!-- Add new -->
              <v-card flat class="add-row mb-4">
                <v-row dense align="center">
                  <v-col cols="9">
                    <v-text-field
                      v-model="newTemplate"
                      label="Text șablon nou"
                      outlined
                      dense
                      hide-details
                      placeholder="ex: {denumirea} nu face față cerințelor actuale"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-btn
                      color="#2d8659"
                      dark
                      block
                      small
                      :disabled="!newTemplate.trim()"
                      @click="addTemplate"
                      :loading="savingTemplate"
                    >
                      <v-icon left small>mdi-plus</v-icon>
                      Adaugă
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card>

              <!-- List -->
              <div v-if="templates.length === 0" class="empty-state">
                Niciun șablon. Adăugați primul șablon mai sus.
              </div>
              <div
                v-for="tmpl in templates"
                :key="tmpl.id"
                class="list-item"
              >
                <template v-if="editingTemplateId === tmpl.id">
                  <v-row dense align="center">
                    <v-col cols="8">
                      <v-text-field
                        v-model="editTemplateValue"
                        outlined
                        dense
                        hide-details
                        autofocus
                        @keyup.enter="saveTemplate(tmpl)"
                        @keyup.esc="cancelEditTemplate"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="4" class="d-flex">
                      <v-btn icon small color="success" @click="saveTemplate(tmpl)" class="mr-1">
                        <v-icon small>mdi-check</v-icon>
                      </v-btn>
                      <v-btn icon small @click="cancelEditTemplate">
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </template>
                <template v-else>
                  <div class="list-item-content">
                    <span class="list-item-text">{{ tmpl.template_text }}</span>
                    <div class="list-item-actions">
                      <v-btn icon x-small @click="startEditTemplate(tmpl)">
                        <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon x-small @click="removeTemplate(tmpl)">
                        <v-icon small color="red lighten-1">mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </v-tab-item>

          <!-- ═══ TAB 3: Experts & Ordin ═══ -->
          <v-tab-item>
            <div class="tab-content">
              <!-- Ordinul rectorului & Setări generale -->
              <div class="section-label-small mb-2">
                <v-icon small color="#2d8659" class="mr-1">mdi-gavel</v-icon>
                Ordinul rectorului & Setări generale
              </div>
              <v-card flat class="add-row mb-4">
                <v-row dense align="center">
                  <v-col cols="5">
                    <v-text-field
                      v-model="ordinNr"
                      label="Nr. ordin"
                      outlined
                      dense
                      hide-details
                      placeholder="ex: 1-50"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="5">
                    <v-text-field
                      v-model="ordinData"
                      label="Data ordin"
                      outlined
                      dense
                      hide-details
                      placeholder="ex: 29.09.2025"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="2">
                    <v-btn
                      color="#2d8659"
                      dark
                      block
                      small
                      @click="saveOrdin"
                      :loading="savingOrdin"
                    >
                      <v-icon left small>mdi-content-save</v-icon>
                      Salvează
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row dense align="center" class="mt-2">
                  <v-col cols="4">
                    <v-text-field
                      v-model="rectorName"
                      label="Numele rectorului"
                      outlined
                      dense
                      hide-details
                      placeholder="ex: Natalia Gașițoi"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="4">
                    <v-text-field
                      v-model="contabilSef"
                      label="Contabil-șef"
                      outlined
                      dense
                      hide-details
                      placeholder="ex: Ana Bejenari"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="4">
                    <v-text-field
                      v-model="codFiscal"
                      label="Codul fiscal"
                      outlined
                      dense
                      hide-details
                      placeholder="ex: 1007602000972"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-card>

              <div class="section-label-small mb-2">
                <v-icon small color="#2d8659" class="mr-1">mdi-account-group</v-icon>
                Experți
              </div>
              <p class="hint-text mb-3">
                Experții apar pe actul de defectare tipărit (nume + funcție).
              </p>

              <!-- Add new -->
              <v-card flat class="add-row mb-4">
                <v-row dense align="center">
                  <v-col cols="5">
                    <v-text-field
                      v-model="newExpertName"
                      label="Nume"
                      outlined
                      dense
                      hide-details
                      placeholder="ex: Ciribaș Valeriu"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="5">
                    <v-text-field
                      v-model="newExpertPosition"
                      label="Funcția"
                      outlined
                      dense
                      hide-details
                      placeholder="ex: adm. de rețea DTI"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="2">
                    <v-btn
                      color="#2d8659"
                      dark
                      block
                      small
                      :disabled="!newExpertName.trim()"
                      @click="addExpert"
                      :loading="savingExpert"
                    >
                      <v-icon left small>mdi-plus</v-icon>
                      Adaugă
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card>

              <!-- List -->
              <div v-if="experts.length === 0" class="empty-state">
                Niciun expert. Adăugați primul expert mai sus.
              </div>
              <div
                v-for="exp in experts"
                :key="exp.id"
                class="list-item"
              >
                <template v-if="editingExpertId === exp.id">
                  <v-row dense align="center">
                    <v-col cols="4">
                      <v-text-field
                        v-model="editExpertName"
                        label="Nume"
                        outlined
                        dense
                        hide-details
                        autofocus
                        @keyup.enter="saveExpert(exp)"
                        @keyup.esc="cancelEditExpert"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="5">
                      <v-text-field
                        v-model="editExpertPosition"
                        label="Funcția"
                        outlined
                        dense
                        hide-details
                        @keyup.enter="saveExpert(exp)"
                        @keyup.esc="cancelEditExpert"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="3" class="d-flex">
                      <v-btn icon small color="success" @click="saveExpert(exp)" class="mr-1">
                        <v-icon small>mdi-check</v-icon>
                      </v-btn>
                      <v-btn icon small @click="cancelEditExpert">
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </template>
                <template v-else>
                  <div class="list-item-content">
                    <span class="list-item-text">
                      <strong>{{ exp.name }}</strong>
                      <span v-if="exp.position" class="grey--text text--darken-1"> — {{ exp.position }}</span>
                    </span>
                    <div class="list-item-actions">
                      <v-btn icon x-small @click="startEditExpert(exp)">
                        <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon x-small @click="removeExpert(exp)">
                        <v-icon small color="red lighten-1">mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </v-tab-item>

          <!-- ═══ TAB 4: PV Commission ═══ -->
          <v-tab-item>
            <div class="tab-content">
              <p class="hint-text mb-3">
                Membrii comisiei pentru Procesul-Verbal de casare. Primul membru este președintele comisiei.
              </p>

              <!-- Add new -->
              <v-card flat class="add-row mb-4">
                <v-row dense align="center">
                  <v-col cols="5">
                    <v-text-field
                      v-model="newPvName"
                      label="Nume"
                      outlined
                      dense
                      hide-details
                      placeholder="ex: Parahonco Alexandr"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="5">
                    <v-text-field
                      v-model="newPvPosition"
                      label="Funcția"
                      outlined
                      dense
                      hide-details
                      placeholder="ex: șeful DTI"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="2">
                    <v-btn
                      color="#2d8659"
                      dark
                      block
                      small
                      :disabled="!newPvName.trim()"
                      @click="addPvMember"
                      :loading="savingPv"
                    >
                      <v-icon left small>mdi-plus</v-icon>
                      Adaugă
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card>

              <!-- List -->
              <div v-if="pvMembers.length === 0" class="empty-state">
                Niciun membru. Adăugați primul membru (va fi președintele comisiei).
              </div>
              <div
                v-for="(m, idx) in pvMembers"
                :key="m.id"
                class="list-item"
              >
                <template v-if="editingPvId === m.id">
                  <v-row dense align="center">
                    <v-col cols="4">
                      <v-text-field
                        v-model="editPvName"
                        label="Nume"
                        outlined
                        dense
                        hide-details
                        autofocus
                        @keyup.enter="savePvMember(m)"
                        @keyup.esc="cancelEditPv"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="5">
                      <v-text-field
                        v-model="editPvPosition"
                        label="Funcția"
                        outlined
                        dense
                        hide-details
                        @keyup.enter="savePvMember(m)"
                        @keyup.esc="cancelEditPv"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="3" class="d-flex">
                      <v-btn icon small color="success" @click="savePvMember(m)" class="mr-1">
                        <v-icon small>mdi-check</v-icon>
                      </v-btn>
                      <v-btn icon small @click="cancelEditPv">
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </template>
                <template v-else>
                  <div class="list-item-content">
                    <span class="list-item-text">
                      <v-chip v-if="idx === 0" x-small color="#2d8659" text-color="white" class="mr-2">Președinte</v-chip>
                      <strong>{{ m.name }}</strong>
                      <span v-if="m.position" class="grey--text text--darken-1"> — {{ m.position }}</span>
                    </span>
                    <div class="list-item-actions">
                      <v-btn icon x-small @click="startEditPv(m)">
                        <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon x-small @click="removePvMember(m)">
                        <v-icon small color="red lighten-1">mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
      <v-dialog :value="!!pendingDelete" persistent max-width="480">
        <DeleteCard
          v-if="pendingDelete"
          :title="pendingDelete.title"
          @cancel="answerDelete(false)"
          @confirm="answerDelete(true)"
        />
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import * as api from "./api";
import * as helper from "@/helper/helper";
import DeleteCard from "@/components/elements/DeleteCard.vue";

export default {
  name: "DecontareSettings",
  components: { DeleteCard },
  data() {
    return {
      dialog: true,
      pendingDelete: null,
      activeTab: 0,
      // Categories
      categories: [],
      newCategory: "",
      newCategoryCod: "",
      savingCategory: false,
      editingCategoryId: null,
      editCategoryValue: "",
      editCategoryCod: "",
      // Templates
      templates: [],
      newTemplate: "",
      savingTemplate: false,
      editingTemplateId: null,
      editTemplateValue: "",
      // Ordin & Settings
      ordinNr: "",
      ordinData: "",
      rectorName: "",
      contabilSef: "",
      codFiscal: "",
      savingOrdin: false,
      // Experts
      experts: [],
      newExpertName: "",
      newExpertPosition: "",
      savingExpert: false,
      editingExpertId: null,
      editExpertName: "",
      editExpertPosition: "",
      // PV Commission
      pvMembers: [],
      newPvName: "",
      newPvPosition: "",
      savingPv: false,
      editingPvId: null,
      editPvName: "",
      editPvPosition: "",
    };
  },
  watch: {
    activeTab() {
      helper.scrollToTop(this);
    },
  },
  methods: {
    // the app's delete card instead of the browser's confirm(); resolves true on "Șterge"
    askDelete(title) {
      return new Promise((resolve) => {
        this.pendingDelete = { title, resolve };
      });
    },
    answerDelete(ok) {
      const pending = this.pendingDelete;
      this.pendingDelete = null;
      if (pending) pending.resolve(ok);
    },
    // ── Categories ──
    async fetchCategories() {
      this.categories = await api.getCategories();
    },
    async addCategory() {
      if (!this.newCategory.trim()) return;
      this.savingCategory = true;
      try {
        await api.createCategory({ denumire: this.newCategory.trim(), cod_catalog: this.newCategoryCod.trim() || null });
        this.newCategory = "";
        this.newCategoryCod = "";
        await this.fetchCategories();
        this.$emit("updated");
      } catch (e) {
        this.$emit("error", "Eroare la adăugarea categoriei");
      }
      this.savingCategory = false;
    },
    startEditCategory(cat) {
      this.editingCategoryId = cat.id;
      this.editCategoryValue = cat.denumire;
      this.editCategoryCod = cat.cod_catalog || "";
    },
    cancelEditCategory() {
      this.editingCategoryId = null;
      this.editCategoryValue = "";
      this.editCategoryCod = "";
    },
    async saveCategory(cat) {
      if (!this.editCategoryValue.trim()) return;
      try {
        await api.updateCategory(cat.id, { denumire: this.editCategoryValue.trim(), cod_catalog: this.editCategoryCod.trim() || null });
        this.cancelEditCategory();
        await this.fetchCategories();
        this.$emit("updated");
      } catch (e) {
        this.$emit("error", "Eroare la editarea categoriei");
      }
    },
    async removeCategory(cat) {
      if (!(await this.askDelete(`Ștergeți categoria „${cat.denumire}”?`))) return;
      try {
        await api.deleteCategory(cat.id);
        await this.fetchCategories();
        this.$emit("updated");
      } catch (e) {
        this.$emit("error", "Eroare la ștergerea categoriei (posibil utilizată în formulare)");
      }
    },

    // ── Templates ──
    async fetchTemplates() {
      this.templates = await api.getTemplates();
    },
    async addTemplate() {
      if (!this.newTemplate.trim()) return;
      this.savingTemplate = true;
      try {
        await api.createTemplate({ template_text: this.newTemplate.trim() });
        this.newTemplate = "";
        await this.fetchTemplates();
        this.$emit("updated");
      } catch (e) {
        this.$emit("error", "Eroare la adăugarea șablonului");
      }
      this.savingTemplate = false;
    },
    startEditTemplate(tmpl) {
      this.editingTemplateId = tmpl.id;
      this.editTemplateValue = tmpl.template_text;
    },
    cancelEditTemplate() {
      this.editingTemplateId = null;
      this.editTemplateValue = "";
    },
    async saveTemplate(tmpl) {
      if (!this.editTemplateValue.trim()) return;
      try {
        await api.updateTemplate(tmpl.id, { template_text: this.editTemplateValue.trim() });
        this.cancelEditTemplate();
        await this.fetchTemplates();
        this.$emit("updated");
      } catch (e) {
        this.$emit("error", "Eroare la editarea șablonului");
      }
    },
    async removeTemplate(tmpl) {
      if (!confirm("Ștergeți acest șablon?")) return;
      try {
        await api.deleteTemplate(tmpl.id);
        await this.fetchTemplates();
        this.$emit("updated");
      } catch (e) {
        this.$emit("error", "Eroare la ștergerea șablonului");
      }
    },

    // ── Settings (Ordin) ──
    async fetchSettings() {
      try {
        const settings = await api.getSettings();
        this.ordinNr = settings.ordin_nr || "";
        this.ordinData = settings.ordin_data || "";
        this.rectorName = settings.rector_name || "";
        this.contabilSef = settings.contabil_sef || "";
        this.codFiscal = settings.cod_fiscal || "";
      } catch (e) { /* ignore */ }
    },
    async saveOrdin() {
      this.savingOrdin = true;
      try {
        await api.updateSettings({
          ordin_nr: this.ordinNr,
          ordin_data: this.ordinData,
          rector_name: this.rectorName,
          contabil_sef: this.contabilSef,
          cod_fiscal: this.codFiscal,
        });
        this.$emit("updated");
      } catch (e) {
        this.$emit("error", "Eroare la salvarea setărilor");
      }
      this.savingOrdin = false;
    },

    // ── Experts ──
    async fetchExperts() {
      this.experts = await api.getExperts();
    },
    async addExpert() {
      if (!this.newExpertName.trim()) return;
      this.savingExpert = true;
      try {
        await api.createExpert({
          name: this.newExpertName.trim(),
          position: this.newExpertPosition.trim() || null,
          sort_order: this.experts.length,
        });
        this.newExpertName = "";
        this.newExpertPosition = "";
        await this.fetchExperts();
        this.$emit("updated");
      } catch (e) {
        this.$emit("error", "Eroare la adăugarea expertului");
      }
      this.savingExpert = false;
    },
    startEditExpert(exp) {
      this.editingExpertId = exp.id;
      this.editExpertName = exp.name;
      this.editExpertPosition = exp.position || "";
    },
    cancelEditExpert() {
      this.editingExpertId = null;
      this.editExpertName = "";
      this.editExpertPosition = "";
    },
    async saveExpert(exp) {
      if (!this.editExpertName.trim()) return;
      try {
        await api.updateExpert(exp.id, {
          name: this.editExpertName.trim(),
          position: this.editExpertPosition.trim() || null,
        });
        this.cancelEditExpert();
        await this.fetchExperts();
        this.$emit("updated");
      } catch (e) {
        this.$emit("error", "Eroare la editarea expertului");
      }
    },
    async removeExpert(exp) {
      if (!(await this.askDelete(`Ștergeți expertul „${exp.name}”?`))) return;
      try {
        await api.deleteExpert(exp.id);
        await this.fetchExperts();
        this.$emit("updated");
      } catch (e) {
        this.$emit("error", "Eroare la ștergerea expertului");
      }
    },

    // ── PV Commission ──
    async fetchPvMembers() {
      this.pvMembers = await api.getPvCommission();
    },
    async addPvMember() {
      if (!this.newPvName.trim()) return;
      this.savingPv = true;
      try {
        await api.createPvCommission({
          name: this.newPvName.trim(),
          position: this.newPvPosition.trim() || null,
          sort_order: this.pvMembers.length,
        });
        this.newPvName = "";
        this.newPvPosition = "";
        await this.fetchPvMembers();
        this.$emit("updated");
      } catch (e) {
        this.$emit("error", "Eroare la adăugarea membrului");
      }
      this.savingPv = false;
    },
    startEditPv(m) {
      this.editingPvId = m.id;
      this.editPvName = m.name;
      this.editPvPosition = m.position || "";
    },
    cancelEditPv() {
      this.editingPvId = null;
      this.editPvName = "";
      this.editPvPosition = "";
    },
    async savePvMember(m) {
      if (!this.editPvName.trim()) return;
      try {
        await api.updatePvCommission(m.id, {
          name: this.editPvName.trim(),
          position: this.editPvPosition.trim() || null,
        });
        this.cancelEditPv();
        await this.fetchPvMembers();
        this.$emit("updated");
      } catch (e) {
        this.$emit("error", "Eroare la editarea membrului");
      }
    },
    async removePvMember(m) {
      if (!(await this.askDelete(`Ștergeți membrul „${m.name}”?`))) return;
      try {
        await api.deletePvCommission(m.id);
        await this.fetchPvMembers();
        this.$emit("updated");
      } catch (e) {
        this.$emit("error", "Eroare la ștergerea membrului");
      }
    },

    close() {
      this.dialog = false;
      this.$emit("close");
    },
  },
  async mounted() {
    await Promise.all([this.fetchCategories(), this.fetchTemplates(), this.fetchExperts(), this.fetchSettings(), this.fetchPvMembers()]);
  },
};
</script>

<style scoped>
.settings-card {
  border-radius: 12px !important;
  overflow: hidden;
}
.settings-header {
  background-color: #2d8659;
  color: white;
  padding: 14px 24px;
}
/* same height for every tab, so switching doesn't move the dialog */
.settings-body {
  padding: 20px 24px !important;
  height: 560px;
  background-color: #fafafa;
}
.tab-content {
  padding-top: 16px;
}
.add-row {
  background: white;
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}
.list-item {
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 6px;
  transition: box-shadow 0.15s;
}
.list-item:hover {
  box-shadow: 0 1px 6px rgba(0,0,0,0.08);
}
.list-item-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.list-item-text {
  font-size: 14px;
  flex: 1;
  margin-right: 8px;
}
.list-item-actions {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
}
.empty-state {
  text-align: center;
  color: #9e9e9e;
  font-style: italic;
  padding: 24px 0;
}
.hint-text {
  font-size: 13px;
  color: #757575;
  background: #f0f7f2;
  padding: 8px 12px;
  border-radius: 6px;
  border-left: 3px solid #2d8659;
}
.section-label-small {
  font-size: 13px;
  font-weight: 600;
  color: #2d8659;
}
.hint-text code {
  background: #e8f5e9;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 12px;
  color: #2e7d32;
}
</style>
